import { useState } from "react";
import { ArrowRight, Check, Mail, MapPin, CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import Seo from "@/components/common/Seo";
import PageHeader from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = ["India", "United Arab Emirates", "Saudi Arabia", "Oman", "Bahrain", "Qatar", "Kuwait", "Singapore", "United Kingdom", "United States", "Other"];
const INDUSTRIES = ["Manufacturing", "Engineering", "Industrial Products", "Automotive", "Retail & Consumer Goods", "Healthcare", "Pharmaceutical", "Infrastructure & Construction", "Logistics & Distribution", "Family-Owned Business", "Other"];
const EMPLOYEES = ["1 – 50", "51 – 200", "201 – 500", "501 – 1,000", "1,000+"];
const CHALLENGES = ["Fractional CIO Leadership", "Technology Strategy", "AI Strategy", "Digital Transformation", "Technology Governance", "ERP Strategy", "Technology Due Diligence", "Vendor Evaluation", "Business Growth Advisory", "Executive Advisory", "Other"];
const TIMES = ["Morning (09:00 – 12:00)", "Afternoon (12:00 – 16:00)", "Evening (16:00 – 19:00)", "Flexible"];

const HOW_WE_HELP = [
  "Fractional CIO",
  "Technology Strategy",
  "AI Strategy",
  "Digital Transformation",
  "Technology Governance",
  "Business Growth",
  "ERP Strategy",
  "Technology Due Diligence",
  "Vendor Evaluation",
  "Executive Advisory",
];

const REGIONS = ["India", "UAE", "Gulf Region", "Global Virtual Advisory"];

const FAQS = [
  { q: "How quickly can we schedule a meeting?", a: "Most executive sessions are scheduled within two to three working days of a completed enquiry. Where the matter is time-sensitive, we will always try to accommodate a faster meeting." },
  { q: "What happens after submitting the form?", a: "You will receive a personal acknowledgement within one business day, followed by a short call or written exchange to confirm the agenda and the most useful format for the conversation." },
  { q: "Do you work with companies outside India?", a: "Yes. Aftab advises leadership teams across India, the UAE and the wider Gulf Region, and works virtually with clients in other markets when the mandate is a fit." },
  { q: "Do you offer virtual advisory sessions?", a: "Yes. Advisory engagements are delivered on a blended basis: virtual sessions via Microsoft Teams or Google Meet by default, with onsite time where the business benefits from it." },
  { q: "Can you work alongside our existing IT team?", a: "Yes. The role of an Executive Technology Advisor is to lead and elevate your existing team, not to replace it. That means setting direction, raising the standard of delivery and giving your people a senior leader to work alongside." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "", company: "", designation: "", email: "", country: "", industry: "",
    employees: "", challenge: "", time: "", message: "",
  });
  const [date, setDate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const details = [
        form.designation && `Designation: ${form.designation}`,
        form.country && `Country: ${form.country}`,
        form.industry && `Industry: ${form.industry}`,
        form.employees && `Employees: ${form.employees}`,
        form.challenge && `Business challenge: ${form.challenge}`,
        date && `Preferred date: ${format(date, "yyyy-MM-dd")}`,
        form.time && `Preferred time: ${form.time}`,
      ].filter(Boolean).join("\n");
      const payload = {
        type: "contact",
        name: form.name,
        email: form.email,
        company: form.company,
        message: `${form.message}\n\n---\n${details}`.trim(),
      };
      await axios.post(`${API}/leads`, payload);
      setDone(true);
      toast.success("Message received. We will be in touch within one business day.");
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact"
        description={`Contact ${BRAND.name}, Executive Technology Advisor and Fractional CIO. Confidential executive conversations for business owners, CEOs and leadership teams across India, the UAE and the Gulf Region.`}
        path="/contact"
        keywords={["Contact Executive Technology Advisor", "Fractional CIO India", "Fractional CIO UAE", "Fractional CIO Gulf", "Virtual CIO", "Executive Advisory Contact", "Family-Owned Business Advisor"]}
        jsonLd={faqJsonLd}
      />
      <PageHeader
        overline="Contact"
        titleLines={["Let's start a strategic", "conversation."]}
        subtitle="Every growing business reaches a stage where technology decisions influence growth, operational efficiency and long-term success. Whether you are planning expansion, evaluating AI, modernising enterprise systems or looking for experienced executive technology leadership, let's discuss how practical advisory can support your business objectives."
        testId="contact-header"
      />

      <section data-testid="contact-section" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="rounded-[1.75rem] border border-slate-200 bg-surface p-8">
                <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">How we can help</p>
                <ul className="mt-6 space-y-3">
                  {HOW_WE_HELP.map((h) => (
                    <li key={h} className="flex items-start gap-3">
                      <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-navy text-white">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="font-body text-slate-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 rounded-[1.75rem] border border-slate-200 bg-white p-8">
                <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Advisory across</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {REGIONS.map((r) => (
                    <span key={r} data-testid={`contact-region-${r.toLowerCase().replace(/\s/g, "-")}`} className="rounded-full bg-navy/5 px-4 py-2 font-body text-sm font-semibold text-navy">{r}</span>
                  ))}
                </div>
              </div>
              <div className="mt-6 space-y-4">
                <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link" className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:border-royal">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy"><Mail className="h-5 w-5" strokeWidth={1.5} /></div>
                  <div>
                    <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Email</p>
                    <p className="mt-1 font-heading font-semibold text-navy">{BRAND.email}</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy"><MapPin className="h-5 w-5" strokeWidth={1.5} /></div>
                  <div>
                    <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Based</p>
                    <p className="mt-1 font-heading font-semibold text-navy">{BRAND.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy"><Clock className="h-5 w-5" strokeWidth={1.5} /></div>
                  <div>
                    <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Response</p>
                    <p className="mt-1 font-heading font-semibold text-navy">Within one business day</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <div data-testid="contact-success" className="flex h-full flex-col items-start justify-center rounded-[1.75rem] border border-slate-200 bg-surface p-12">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-navy text-white">
                  <Check className="h-8 w-8" strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-heading text-3xl font-bold tracking-tight text-navy">Thank you, {form.name.split(" ")[0] || "there"}.</h3>
                <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">Your message has been received. Aftab&rsquo;s office will respond within one business day.</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="rounded-[1.75rem] border border-slate-200 bg-surface p-8 md:p-10">
                <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Tell us about your business</p>
                <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy md:text-3xl">A confidential enquiry.</h2>

                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <Field label="Full name" htmlFor="c-name">
                    <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Jane Executive" className="bg-white" />
                  </Field>
                  <Field label="Business email" htmlFor="c-email">
                    <Input id="c-email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" className="bg-white" />
                  </Field>
                  <Field label="Company" htmlFor="c-company">
                    <Input id="c-company" data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Company name" className="bg-white" />
                  </Field>
                  <Field label="Designation" htmlFor="c-designation">
                    <Input id="c-designation" data-testid="contact-designation" value={form.designation} onChange={set("designation")} placeholder="e.g. Managing Director" className="bg-white" />
                  </Field>
                  <Field label="Country" htmlFor="c-country">
                    <Select value={form.country} onValueChange={set("country")}>
                      <SelectTrigger data-testid="contact-country" className="bg-white"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Industry" htmlFor="c-industry">
                    <Select value={form.industry} onValueChange={set("industry")}>
                      <SelectTrigger data-testid="contact-industry" className="bg-white"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>{INDUSTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Number of employees (optional)" htmlFor="c-employees">
                    <Select value={form.employees} onValueChange={set("employees")}>
                      <SelectTrigger data-testid="contact-employees" className="bg-white"><SelectValue placeholder="Select size" /></SelectTrigger>
                      <SelectContent>{EMPLOYEES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Business challenge" htmlFor="c-challenge">
                    <Select value={form.challenge} onValueChange={set("challenge")}>
                      <SelectTrigger data-testid="contact-challenge" className="bg-white"><SelectValue placeholder="Select area" /></SelectTrigger>
                      <SelectContent>{CHALLENGES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Preferred meeting date" htmlFor="c-date">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid="contact-date-trigger" className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-white px-3 py-2 text-left font-body text-sm text-slate-700">
                          <CalendarIcon className="h-4 w-4 text-navy" />
                          {date ? format(date, "PPP") : <span className="text-slate-400">Pick a date</span>}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </Field>
                  <Field label="Preferred meeting time" htmlFor="c-time">
                    <Select value={form.time} onValueChange={set("time")}>
                      <SelectTrigger data-testid="contact-time" className="bg-white"><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent>{TIMES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Message" htmlFor="c-message" className="mt-6">
                  <Textarea id="c-message" data-testid="contact-message" rows={5} required value={form.message} onChange={set("message")} placeholder="Tell us a little about your priorities or the question you would like to discuss." className="bg-white" />
                </Field>
                <button data-testid="contact-submit" disabled={busy} className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors hover:bg-navy disabled:opacity-60 sm:w-auto">
                  {busy ? "Sending…" : "Tell us about your business"}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section data-testid="contact-faq" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Frequently asked questions</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">Straight answers, before we talk.</h2>
          </div>
          <Accordion type="single" collapsible className="mt-14 max-w-4xl border-t border-slate-200">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`contact-faq-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="py-6 font-heading text-xl font-bold text-navy hover:no-underline data-[state=open]:text-royal">{f.q}</AccordionTrigger>
                <AccordionContent className="max-w-3xl font-body text-lg leading-relaxed text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}

function Field({ label, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="font-body text-sm font-medium text-navy">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
